import { useMemo } from 'react';
import type { ProgressItem } from '@/types';

interface ParentProgressSectionProps {
  progress: ProgressItem[];
}

export default function ParentProgressSection({ progress }: ParentProgressSectionProps) {
  const grouped = useMemo(() => {
    const map = new Map<string, ProgressItem[]>();
    progress.forEach((it) => {
      if (!map.has(it.category)) map.set(it.category, []);
      map.get(it.category)!.push(it);
    });
    return Array.from(map.entries());
  }, [progress]);

  const total = progress.length;
  const done = progress.filter((p) => p.status === 'done').length;
  const remaining = total - done;
  const percent = total === 0 ? 0 : Math.round((done / total) * 100);

  return (
    <section className="rounded-xl border border-background-200 bg-background-50 p-5 md:p-6">
      <h2 className="flex flex-wrap items-center gap-2 text-sm font-bold text-foreground-950">
        <span className="flex h-7 w-7 items-center justify-center rounded-lg bg-primary-100 text-primary-700">
          <i className="ri-route-line"></i>
        </span>
        남은 진도
        <span className="rounded-full bg-background-200 px-2.5 py-0.5 text-[11px] font-semibold text-foreground-600">
          완료 {done} / 전체 {total}
        </span>
      </h2>

      {total === 0 ? (
        <p className="mt-4 rounded-lg border border-dashed border-background-300 py-8 text-center text-sm text-foreground-500">
          아직 등록된 진도가 없어요.
        </p>
      ) : (
        <>
          <div className="mt-4 grid grid-cols-3 gap-3">
            <div className="rounded-lg border border-background-200 bg-background-100 p-3.5">
              <p className="text-xs text-foreground-500">진행률</p>
              <p className="mt-1 text-xl font-bold text-primary-700">{percent}%</p>
            </div>
            <div className="rounded-lg border border-background-200 bg-background-100 p-3.5">
              <p className="text-xs text-foreground-500">완료 단원</p>
              <p className="mt-1 text-xl font-bold text-foreground-950">{done}개</p>
            </div>
            <div className="rounded-lg border border-accent-200 bg-accent-50 p-3.5">
              <p className="text-xs text-accent-800">남은 진도</p>
              <p className="mt-1 text-xl font-bold text-accent-900">{remaining}개</p>
            </div>
          </div>

          <div className="mt-3 h-2.5 w-full overflow-hidden rounded-full bg-background-200">
            <div
              className="h-full rounded-full bg-primary-500 transition-all"
              style={{ width: `${percent}%` }}
            />
          </div>

          <div className="mt-4 flex flex-col gap-3">
            {grouped.map(([category, items]) => {
              const cDone = items.filter((i) => i.status === 'done').length;
              const cPercent = Math.round((cDone / items.length) * 100);
              return (
                <div
                  key={category}
                  className="rounded-lg border border-background-200 bg-background-100 p-4"
                >
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <p className="text-sm font-semibold text-foreground-950">{category}</p>
                    <span className="text-xs font-semibold text-primary-700">
                      {cDone}/{items.length} · {cPercent}%
                    </span>
                  </div>
                  <div className="mt-2.5 flex flex-wrap gap-1.5">
                    {items.map((it) => {
                      const isDone = it.status === 'done';
                      return (
                        <span
                          key={it.id}
                          className={`flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-medium ${
                            isDone
                              ? 'bg-primary-100 text-primary-700'
                              : 'border border-background-300 bg-background-50 text-foreground-500'
                          }`}
                        >
                          <i
                            className={
                              isDone ? 'ri-checkbox-circle-fill' : 'ri-checkbox-blank-circle-line'
                            }
                          ></i>
                          {it.unit}
                        </span>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        </>
      )}
    </section>
  );
}